/**
 * When a patient may take the symptom assessment for an appointment.
 *
 * Shared so the home page's appointment cards and the symptom chart's button
 * cannot disagree — they were previously two separate rules, and only one of
 * them was reachable for a diagnosis the patient had never been treated for.
 */

/** How far ahead of a visit the assessment opens. */
export const ASSESSMENT_WINDOW_DAYS = 7;

const startOfDay = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Compared by calendar day rather than by exact instant.
 *
 * The old check required the appointment to be in the future, so an appointment
 * booked for earlier today was already ineligible — a patient booking a same-day
 * slot could not assess for it at all.
 */
export const isWithinAssessmentWindow = (appt) => {
  if (!appt || appt.status === 'CANCELED') return false;

  const apptDay = startOfDay(appt.apptStartTime);
  const today = startOfDay(new Date());
  if (!apptDay || !today) return false;

  const days = (apptDay - today) / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= ASSESSMENT_WINDOW_DAYS;
};

/**
 * Whether this appointment already has an assessment.
 *
 * The appointment carries its own score, resolved server-side by appointment id
 * (AppointmentMapper.getSymptomScore -> findByApptId), and is left at 0 when
 * none exists. That is the authoritative per-visit answer; inferring it from how
 * recently any score was taken locked out patients returning for a condition
 * they had been assessed for before.
 */
export const hasAssessmentForAppt = (appt) => Number(appt?.symptomScore ?? 0) !== 0;

/** An appointment the patient can assess right now. */
export const canAssessAppt = (appt) => isWithinAssessmentWindow(appt) && !hasAssessmentForAppt(appt);
